import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import Button from "@/components/Form/Button/Button";
import PageTitle from "@/components/Form/PageTitle/PageTitle";
import {
  MotifAccordion,
  MotifAccordionContent,
  MotifAccordionTrigger,
  MotifButton,
  MotifCheckbox,
  MotifErrorMessage,
  MotifIcon,
  MotifProgressLoader,
} from "@ey-xd/motif-react";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconoirArrowLeft } from "@ey-xd/motif-icon";
import ControlInputBox from "./ControlInputBoxUnit";
import {
  UNIT_NAME,
  UNIT_NAME_FRENCH,
  UNIT_RATE_NAME,
  UNIT_DECIMAL_PLACE_NAME,
  UNIT_DESCRIPTION_NAME,
  UNIT_DESCRIPTION_NAME_FRENCH,
  IS_DEFAULT_CURRENCY_NAME,
} from "./Constants";
import { UnitDetailsForm } from "./type";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { postUnitConfiguration } from "./UnitSetupHelper";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import {
  useCreateUnitMutation,
  useUpdateUnitMutation,
} from "@/services/unit/unitReducers";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import ControlTextAreaWithCharLimit from "@/components/Form/InputBox/ControlTextAreaWithCharLimit";
import { useCase } from "@/features/case/use-case";
import { useDispatch } from "react-redux";
import { setDiscardEditMode } from "@/components/Modal/ConfirmationModal/discardchanges";
import { resetCase } from "@/features/case/case-slice";
import { useSelector } from "react-redux";
import { havingEditPermission } from "@/utility/common";
import {
  CANCEL_BUTTON_VARIANT,
  SAVE_BUTTON_VARIANT,
} from "@/components/Images/CommonImages";
import { ActiveTabsOrder } from "@/menuData";
import ControlInputNumberBoxUnit from "./ControlInputNumberBoxUnit";
export default function UnitSetup() {
  const [isDiscardVisible, setIsDiscardVisible] = useState(false);
  // @ts-ignore
  // const [isCancel, setIsCancel] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const caseData = useCase();
  const { caseId, caseName } = caseData;
  const data = location.state?.data;
  const permissions = useSelector((state: any) => state.permissions);
  const { Unit } = permissions;
  const isNewUnit = data === undefined ? true : false;
  const pageStatus = isNewUnit ? false : !havingEditPermission(Unit);

  const [showFrenchCaseSection, setShowFrenchCaseSection] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState(true);
  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    getValues,
    setValue,
  } = useForm<UnitDetailsForm>({
    defaultValues: {
      [UNIT_NAME]: data?.name || "",
      [UNIT_RATE_NAME]: data?.exchangeRate || "",
      [UNIT_DECIMAL_PLACE_NAME]: data?.decimalPlace || "",
      [UNIT_DESCRIPTION_NAME]: data?.description || "",
      [UNIT_NAME_FRENCH]: data?.nameFrench || "",
      [IS_DEFAULT_CURRENCY_NAME]: data?.isDefaultCurrency || false,
      [UNIT_DESCRIPTION_NAME_FRENCH]: data?.descriptionFrench || "",
    },
  });

  const unitData = caseData?.caseUnits;
  const [updateUnit, { isLoading: updateLoading }] = useUpdateUnitMutation();
  const [createUnit, { isLoading: createLoading }] = useCreateUnitMutation();

  const [isDefaultCurrency] = useState(data?.isDefaultCurrency || false);
  const [isFrenchCaseChecked, setIsFrenchCaseChecked] = useState(
    data?.isFrenchCaseChecked || false
  );
  useEffect(() => {
    if (data?.nameFrench || data?.descriptionFrench) {
      setIsFrenchCaseChecked(true);
      setShowFrenchCaseSection(true);
    }
  }, [data, data?.nameFrench, data?.descriptionFrench]);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [showMessage, setShowMessage] = useState({
    show: false,
    type: "error",
    message: "",
  });

  const unitNameExists = useCallback(
    (unitName: string) => {
      const lowerCaseUnitName = unitName.toLowerCase();
      return (
        unitData &&
        !unitData.some(
          (unit: { name: string }) =>
            unit.name.toLowerCase() === lowerCaseUnitName
        )
      );
    },
    [unitData]
  );

  const onSubmit = async (formData: UnitDetailsForm) => {
    setNotificationVisible(false);
    try {
      const fullData = {
        ...formData,
        unitRate: isDefaultCurrency ? "1" : formData.unitRate,
        isDefaultCurrency: isDefaultCurrency,
        isFrenchCaseChecked: isFrenchCaseChecked,
        caseId: caseId,
        isNewUnit: isNewUnit,
      };
      const dataWithID =
        isNewUnit === true ? fullData : { ...fullData, id: data.id };
      dispatch(setDiscardEditMode({ editMode: false }));
      let unitData = postUnitConfiguration(dataWithID);
      if (!isFrenchCaseChecked) {
        unitData = {
          ...unitData,
          nameFrench: "",
          descriptionFrench: "",
        };
      }
      if (isNewUnit) {
        const response: any = await createUnit(unitData);
        if (response.hasOwnProperty("data")) {
          dispatch(resetCase());
          setShowMessage({
            ...showMessage,
            show: true,
            type: "success",
            //@ts-ignore
            message: t("global.globalNotifications.createdSuccessfully"),
          });
          setNotificationVisible(true);
          navigate(
            `/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`
          );
        } else {
          setShowMessage({
            ...showMessage,
            show: true,
            type: "error",
            //@ts-ignore
            message:
              response?.error?.data?.message ||
              t("global.globalNotifications.unExpectedError"),
          });
          setNotificationVisible(true);
        }
      } else {
        const response: any = await updateUnit(unitData);
        if (response.hasOwnProperty("data")) {
          dispatch(resetCase());
          setShowMessage({
            ...showMessage,
            show: true,
            type: "success",
            //@ts-ignore
            message: t("global.globalNotifications.updatedSuccessfully"),
          });
          setNotificationVisible(true);
          navigate(
            `/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`
          );
        } else {
          setShowMessage({
            ...showMessage,
            show: true,
            type: "error",
            //@ts-ignore
            message:
              response?.error?.data?.message ||
              t("global.globalNotifications.unExpectedError"),
          });
          setNotificationVisible(true);
        }
      }
    } catch (error) {
      dispatch(setDiscardEditMode({ editMode: true }));
    }
  };

  useEffect(() => {
    if (isDefaultCurrency) {
      setValue(UNIT_RATE_NAME, "1");
    } else {
      setValue(UNIT_RATE_NAME, getValues(UNIT_RATE_NAME));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDefaultCurrency]);

  const discardCancelBtnHandler = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    setIsDiscardVisible(false);
    dispatch(setDiscardEditMode({ editMode: true }));
  };

  const discardConfirmBtnHandler = (event: any) => {
    event.preventDefault();
    navigate(`/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`);
    setIsDiscardVisible(false);
  };

  const handleFrenchCheckboxSelection = () => {
    setIsFrenchCaseChecked(!isFrenchCaseChecked);
    setShowFrenchCaseSection(!showFrenchCaseSection);
  };

  const handleUnitCancel = (e: any) => {
    if (isDirty) {
      dispatch(setDiscardEditMode({ editMode: false }));

      setIsDiscardVisible(true);
    } else {
      discardConfirmBtnHandler(e);
    }
  };
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setDiscardEditMode({ editMode: isDirty }));
    return () => {
      dispatch(setDiscardEditMode({ editMode: false }));
    };
  }, [dispatch, isDirty]);

  return (
    <>
      {notificationVisible && (
        <NotificationGrid
          show={true}
          message={"global.globalNotifications.updatedSuccessfully"}
          type="success"
        />
      )}
      {caseName ? (
        <Breadcrumb
          items={
            isNewUnit
              ? [
                  {
                    text: caseName,
                    url: `/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`,
                    icon: <MotifIcon src={IconoirArrowLeft} />,
                  },
                  {
                    text: t("pages.configuration.units.newUnit"),
                    url: `/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`,
                    icon: <MotifIcon src={IconoirArrowLeft} />,
                    disabled: true,
                  },
                ]
              : [
                  {
                    text: caseName,
                    url: `/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`,
                    icon: <MotifIcon src={IconoirArrowLeft} />,
                  },
                  {
                    text: data?.name,
                    url: "/",
                    icon: <MotifIcon src={IconoirArrowLeft} />,
                    disabled: true,
                  },
                ]
          }
          ariaLabel="Breadcrumb navigation"
        />
      ) : (
        <MotifProgressLoader variant="default"></MotifProgressLoader>
      )}

      <PageTitle
        title={"pages.configuration.units.createUnit.primaryTitle"}
        description={"pages.configuration.units.createUnit.primaryDescription"}
      />

      {showMessage.show && (
        <NotificationGrid
          show={true}
          message={showMessage.message}
          type={showMessage.type}
        />
      )}
      <MotifAccordion
        alignIconRight={true}
        useChevronIcon={true}
        className="accordion"
        onClose={() => {
          setAccordionOpen(false);
        }}
        onOpen={() => {
          setAccordionOpen(true);
        }}
        open={accordionOpen}
      >
        <MotifAccordionTrigger className="accordion-trigger-button">
          {t("pages.configuration.units.formLabels.unitIdentification")}{" "}
        </MotifAccordionTrigger>
        <MotifAccordionContent className="pad-h-0 pad-v-20">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="motif-container pad-h-0">
              <div className="motif-row mar-b-30">
                <div className="motif-col-lg-6 motif-col-md-8">
                  <ControlInputBox
                    name={UNIT_NAME}
                    disabled={pageStatus}
                    control={control}
                    required={true}
                    isTextArea={false}
                    label={"pages.configuration.units.formLabels.unitName"}
                    showLabel={true}
                    value={data?.unit || ""}
                    rules={
                      isNewUnit
                        ? {
                            validate: {
                              unitNameExists: (value: any) =>
                                unitNameExists(value),
                              invalidUnit: (value: {
                                trim: () => {
                                  (): any;
                                  new (): any;
                                  length: number;
                                };
                              }) => value.trim().length > 0,
                            },
                          }
                        : {}
                    }
                  />
                  {errors[UNIT_NAME] &&
                    errors[UNIT_NAME].type === "required" && (
                      <MotifErrorMessage>
                        {t(
                          "pages.configuration.units.validationMessages.unitNameRequired"
                        )}
                      </MotifErrorMessage>
                    )}

                  {errors[UNIT_NAME] &&
                    errors[UNIT_NAME].type === "unitNameExists" && (
                      <MotifErrorMessage>
                        {t(
                          "pages.configuration.units.validationMessages.unitExists"
                        )}
                      </MotifErrorMessage>
                    )}
                  {errors[UNIT_NAME] &&
                    errors[UNIT_NAME].type === "invalidUnit" && (
                      <MotifErrorMessage>
                        {t(
                          "pages.configuration.units.validationMessages.validName"
                        )}
                      </MotifErrorMessage>
                    )}
                </div>
              </div>
              <div className="motif-row mar-b-30">
                <div className="motif-col-lg-6 motif-col-md-8">
                  <ControlInputNumberBoxUnit
                    name={UNIT_RATE_NAME}
                    control={control}
                    required={true}
                    isTextArea={false}
                    label={"pages.configuration.units.formLabels.rate"}
                    showLabel={true}
                    value={data?.exchangeRate || ""}
                    disabled={pageStatus || isDefaultCurrency}
                    rules={{
                      pattern: {
                        value: /^[.0-9]+$/,
                      },
                    }}
                  />
                  {isDefaultCurrency && (
                    <div className="motif-caption-italic-regular">
                      {t(
                        "pages.configuration.units.validationMessages.exchangeRateMessage"
                      )}
                    </div>
                  )}
                  {errors[UNIT_RATE_NAME] &&
                    errors[UNIT_RATE_NAME].type === "required" && (
                      <MotifErrorMessage>
                        {t(
                          "pages.configuration.units.validationMessages.rateRequired"
                        )}
                      </MotifErrorMessage>
                    )}

                  {errors[UNIT_RATE_NAME] &&
                    errors[UNIT_RATE_NAME].type === "pattern" && (
                      <MotifErrorMessage>
                        {t(
                          "pages.configuration.units.validationMessages.rateMustNumber"
                        )}
                      </MotifErrorMessage>
                    )}
                  {errors[UNIT_RATE_NAME] &&
                    errors[UNIT_RATE_NAME].type === "defaultCurrencyRate" && (
                      <MotifErrorMessage>
                        {errors[UNIT_RATE_NAME].message}
                      </MotifErrorMessage>
                    )}
                </div>
              </div>
              <div className="motif-row mar-b-30">
                <div className="motif-col-lg-6 motif-col-md-8">
                  <ControlInputNumberBoxUnit
                    name={UNIT_DECIMAL_PLACE_NAME}
                    disabled={pageStatus}
                    control={control}
                    required={true}
                    isTextArea={false}
                    label={"pages.configuration.units.formLabels.decimalRate"}
                    showLabel={true}
                    value={data?.decimalPlace || ""}
                    rules={{
                      pattern: {
                        value: /^[0-9]+$/,
                      },
                    }}
                  />
                  {errors[UNIT_DECIMAL_PLACE_NAME] &&
                    errors[UNIT_DECIMAL_PLACE_NAME].type === "required" && (
                      <MotifErrorMessage>
                        {t(
                          "pages.configuration.units.validationMessages.decimalPlaceRequired"
                        )}
                      </MotifErrorMessage>
                    )}

                  {errors[UNIT_DECIMAL_PLACE_NAME] &&
                    errors[UNIT_DECIMAL_PLACE_NAME].type === "pattern" && (
                      <MotifErrorMessage>
                        {t(
                          "pages.configuration.units.validationMessages.decimalMustNumber"
                        )}
                      </MotifErrorMessage>
                    )}
                </div>
              </div>
              <div className="motif-row mar-b-30">
                <div className="motif-col-lg-6 motif-col-md-8">
                  {/* <ControlInputBox
                    name={UNIT_DESCRIPTION_NAME}
                    control={control}
                    required={false}
                    isTextArea={false}
                    label={t(UNIT_DESCRIPTION_LABEL)}
                    showLabel={true}
                    value="testing"
                  /> */}
                  <ControlTextAreaWithCharLimit
                    name={UNIT_DESCRIPTION_NAME}
                    readonly={pageStatus}
                    control={control}
                    required={false}
                    isTextArea={true}
                    label={
                      "pages.configuration.units.formLabels.unitDescription"
                    }
                    showLabel={true}
                  />
                </div>
              </div>
              <div className="motif-row mar-b-30">
                <div className="motif-col-lg-6 motif-col-md-8">
                  <MotifCheckbox
                    id="french-case-identifiers-checkbox"
                    name="french-case-identifiers-checkbox"
                    onChange={handleFrenchCheckboxSelection}
                    value="french-case-identifiers-checkbox"
                    checked={isFrenchCaseChecked}
                    disabled={pageStatus}
                  >
                    {t("pages.configuration.units.formLabels.includeFrench")}{" "}
                  </MotifCheckbox>
                </div>
              </div>
              {showFrenchCaseSection && (
                <>
                  {/* <div className="motif-row mar-b-30 justify-content-between align-items-center">
                    <div className="motif-col-2">
                      {t("pages.configuration.units.formLabels.french")}
                    </div>
                    <div className="motif-col-10">
                      <Button
                        label={t(
                          "pages.configuration.units.formLabels.removeSection"
                        )}
                        variant="text"
                        onClickHandler={handleRemoveFrenchSection}
                        className="motif-button motif-body2-underlined-regular "
                      />
                    </div>
                  </div> */}
                  <div className="motif-row mar-b-30">
                    <div className="motif-col-lg-6 motif-col-md-8">
                      <ControlInputBox
                        name={UNIT_NAME_FRENCH}
                        control={control}
                        required={false}
                        disabled={pageStatus}
                        isTextArea={false}
                        label={
                          "pages.configuration.units.formLabels.unitNameFrench"
                        }
                        showLabel={true}
                        value={"Test"}
                      />
                    </div>
                  </div>
                  <div className="motif-row mar-b-30">
                    <div className="motif-col-lg-6 motif-col-md-8">
                      {/* <ControlInputBox
                        name={UNIT_DESCRIPTION_NAME_FRENCH}
                        control={control}
                        required={false}
                        isTextArea={false}
                        label={t(UNIT_DESCRIPTION_LABEL_FRENCH)}
                        showLabel={true}
                        value={"Test"}
                      /> */}
                      <ControlTextAreaWithCharLimit
                        name={UNIT_DESCRIPTION_NAME_FRENCH}
                        readonly={pageStatus}
                        control={control}
                        required={false}
                        isTextArea={true}
                        label={
                          "pages.configuration.units.formLabels.unitDescriptionFrench"
                        }
                        showLabel={true}
                      />
                    </div>
                  </div>
                </>
              )}

              {havingEditPermission(Unit) && (
                <div className="motif-row justify-content-end button-container sticky-footer">
                  <div className="pad-r-10">
                    <MotifButton
                      type="button"
                      variant={CANCEL_BUTTON_VARIANT}
                      className="motif-button btn"
                      onClick={(e) => handleUnitCancel(e)}
                    >
                      {t("global.globalActions.cancel")}
                    </MotifButton>
                  </div>
                  <div className="pad-r-10">
                    <Button
                      label={t("global.globalActions.save")}
                      variant={SAVE_BUTTON_VARIANT}
                      className="motif-button"
                      loading={updateLoading || createLoading}
                      onClickHandler={handleSubmit(onSubmit)}
                    />
                  </div>
                </div>
              )}
            </div>
            <ConfirmationModal
              isVisible={isDiscardVisible}
              setIsVisible={setIsDiscardVisible}
              cancelBtnApproveHandler={discardConfirmBtnHandler}
              cancelBtnRejectHandler={discardCancelBtnHandler}
            />
          </form>
        </MotifAccordionContent>
      </MotifAccordion>
    </>
  );
}
